import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import {
  ArrowBack,
  Assignment,
  Autorenew,
  Biotech,
  Close,
  Medication,
} from "@mui/icons-material";
import {
  Avatar,
  Box,
  Card,
  CardActionArea,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { toast } from "react-toastify";
import { CircularLoading, NoDataWarning } from "@/components/common";
import { usePractitionerRoleContext } from "@/contexts/PractitionerRoleContext";
import { useSessionContext } from "@/contexts/SessionContext";
import { BundlePatientSummary } from "@/services/fhir";
import { HTTPResponseStatus } from "@/utils/enum";
import { useFhirData } from "@/utils/useFhirData";
import { IframeDialog } from "@/pages/ehrrecords/components/IframeDialog";
import { MedicalNotes } from "@/pages/ehrrecords/components/patientSummaryAccordions/Documents/components/MedicalNotes";
import { NoteReport } from "@/pages/ehrrecords/components/patientSummaryAccordions/Documents/components/NoteReport";
import { Solicitations } from "@/pages/ehrrecords/components/patientSummaryAccordions/Documents/components/Solicitations";
import { useProcessedDocumentLists } from "@/pages/ehrrecords/components/patientSummaryAccordions/Documents/useProcessedDocumentLists";
import {
  canReissueCertificate,
  canReissuePrescription,
  canReissueSolicitation,
} from "./canReissueDocument";

/** Tipos de documento disponíveis no atalho de reemissão (alinhados às tabs de Documents). */
export type ReissueDocumentType =
  | "prescription"
  | "solicitation"
  | "certificate";

type DialogStep = "select-type" | "list";

interface DocumentReissueShortcutDialogProps {
  open: boolean;
  onClose: () => void;
  /** Identificador do paciente no mesmo formato de navigate("/summary"). */
  patientId: string;
  patientName?: string;
}

const TYPE_CARDS: Array<{
  type: ReissueDocumentType;
  title: string;
  description: string;
  icon: ReactNode;
}> = [
  {
    type: "prescription",
    title: "Receitas médicas",
    description: "Prescrições assinadas disponíveis para reemissão",
    icon: <Medication color="primary" sx={{ fontSize: 36 }} />,
  },
  {
    type: "solicitation",
    title: "Solicitações de exame",
    description: "Pedidos de exame assinados disponíveis para reemissão",
    icon: <Biotech color="primary" sx={{ fontSize: 36 }} />,
  },
  {
    type: "certificate",
    title: "Relatórios e atestados",
    description: "Relatórios e atestados assinados disponíveis para reemissão",
    icon: <Assignment color="primary" sx={{ fontSize: 36 }} />,
  },
];

/**
 * Atalho da busca de pacientes: escolhe o tipo → lista documentos assinados → reemite
 * reutilizando MedicalNotes / Solicitations / NoteReport (+ Actions existentes).
 */
export function DocumentReissueShortcutDialog({
  open,
  onClose,
  patientId,
  patientName,
}: DocumentReissueShortcutDialogProps) {
  const { access } = useSessionContext();
  const { userPractitionerRole } = usePractitionerRoleContext();

  const [step, setStep] = useState<DialogStep>("select-type");
  const [selectedType, setSelectedType] = useState<ReissueDocumentType | null>(
    null,
  );
  const [loading, setLoading] = useState(false);
  const [summaryData, setSummaryData] = useState<any>();
  const [refetch, setRefetch] = useState(false);

  // Contexto usado só no atalho: lista apenas docs que as Actions deixariam reemitir
  const reissueCtx = useMemo(
    () => ({
      accessType: access?.type,
      practitionerRoleId: userPractitionerRole?.id,
    }),
    [access?.type, userPractitionerRole?.id],
  );

  // Visualização de PDF (mesmo padrão do PatientSummary)
  const [viewerOpen, setViewerOpen] = useState(false);
  const [binaryUrl, setBinaryUrl] = useState("");

  const handleOpenViewer = () => setViewerOpen(true);
  const handleCloseViewer = () => {
    setBinaryUrl("");
    setViewerOpen(false);
  };

  const loadSummary = useCallback(async () => {
    if (!patientId) return;

    setLoading(true);
    try {
      const bundleResponse = await BundlePatientSummary({
        identifier: patientId,
        purposeofuse: "Atendimento",
      });

      if (bundleResponse?.status === HTTPResponseStatus.Forbidden) {
        toast.warn("Verifique as opções de consentimento");
        setSummaryData(undefined);
        return;
      }

      setSummaryData(bundleResponse);
    } catch (err) {
      console.error("[DocumentReissueShortcutDialog] Bundle error:", err);
      toast.error("Não foi possível carregar os documentos do paciente.");
      setSummaryData(undefined);
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  // Carrega o summary ao abrir; refetch após reemissão bem-sucedida
  useEffect(() => {
    if (!open) return;
    loadSummary();
  }, [open, refetch, loadSummary]);

  // Reset de estado ao fechar
  useEffect(() => {
    if (!open) {
      setStep("select-type");
      setSelectedType(null);
      setSummaryData(undefined);
      setBinaryUrl("");
      setViewerOpen(false);
    }
  }, [open]);

  // Extração idêntica ao PatientSummary (documentos assinados)
  const medicalNoteData = useFhirData.extractPatientSummaryResource(
    summaryData,
    "DocumentReference",
    "prescricao",
  )?.[0]?.entry;

  const solicitationData = useFhirData.extractPatientSummaryResource(
    summaryData,
    "DocumentReference",
    "service-request",
  )?.[0]?.entry;

  const NoteReportData = useFhirData.extractPatientSummaryResource(
    summaryData,
    "DocumentReference",
    "atestado",
  )?.[0]?.entry;

  const resultsData = useFhirData.extractPatientSummaryResource(
    summaryData,
    "DocumentReference",
    "resultado",
  );

  const encounterData = useFhirData.extractPatientSummaryResource(
    summaryData,
    "Encounter",
  )?.[0]?.entry;

  // sign=true → ProofOfOrigin; em seguida filtra só os reemitíveis pelo profissional logado
  const {
    processedMedicalNoteData: allMedicalNotes,
    processedSolicitationData: allSolicitations,
    processedNoteReportData: allNoteReports,
  } = useProcessedDocumentLists({
    medicalNoteData,
    solicitationData,
    resultsData,
    NoteReportData,
    sign: true,
    encounterData,
  });

  // Atalho: esconde docs sem botão de reemissão (canSign / canSignOrReissue)
  const processedMedicalNoteData = useMemo(
    () =>
      allMedicalNotes.filter((row: any) =>
        canReissuePrescription(row, reissueCtx),
      ),
    [allMedicalNotes, reissueCtx],
  );

  const processedSolicitationData = useMemo(
    () =>
      allSolicitations.filter((row: any) =>
        canReissueSolicitation(row, reissueCtx),
      ),
    [allSolicitations, reissueCtx],
  );

  const processedNoteReportData = useMemo(
    () =>
      allNoteReports.filter((row: any) =>
        canReissueCertificate(row, reissueCtx),
      ),
    [allNoteReports, reissueCtx],
  );

  const countByType: Record<ReissueDocumentType, number> = {
    prescription: processedMedicalNoteData.length,
    solicitation: processedSolicitationData.length,
    certificate: processedNoteReportData.length,
  };

  const titleByType: Record<ReissueDocumentType, string> = {
    prescription: "Receitas médicas",
    solicitation: "Solicitações de exame",
    certificate: "Relatórios e atestados",
  };

  const handleSelectType = (type: ReissueDocumentType) => {
    setSelectedType(type);
    setStep("list");
  };

  const handleBack = () => {
    setSelectedType(null);
    setStep("select-type");
  };

  const handleClose = () => {
    onClose();
  };

  const dialogTitle =
    step === "list" && selectedType
      ? titleByType[selectedType]
      : "Reemitir documento";

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: { minHeight: { xs: "70vh", sm: 480 } } }}
      >
        <DialogTitle sx={{ pr: 6 }}>
          <Stack spacing={0.5}>
            <Stack direction="row" alignItems="center" spacing={1}>
              {step === "list" ? (
                <IconButton
                  aria-label="Voltar"
                  onClick={handleBack}
                  size="small"
                  edge="start"
                >
                  <ArrowBack />
                </IconButton>
              ) : (
                <Autorenew color="primary" />
              )}
              <Typography variant="h6" component="span" fontWeight={600}>
                {dialogTitle}
              </Typography>
            </Stack>
            {patientName ? (
              <Typography variant="body2" color="text.secondary" sx={{ pl: step === "list" ? 5 : 4.5 }}>
                Paciente: {patientName}
              </Typography>
            ) : null}
          </Stack>
          <IconButton
            aria-label="Fechar"
            onClick={handleClose}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {/* Loading só no primeiro fetch — refetch após reemissão mantém a lista visível */}
          {loading && !summaryData ? (
            <Box py={6}>
              <CircularLoading />
            </Box>
          ) : step === "select-type" ? (
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              sx={{ pt: 1 }}
            >
              {TYPE_CARDS.map((card) => {
                const count = countByType[card.type];
                return (
                  <Card
                    key={card.type}
                    variant="outlined"
                    sx={{
                      flex: 1,
                      borderRadius: 2,
                      borderColor: "divider",
                      transition: "border-color 0.15s, box-shadow 0.15s",
                      "&:hover": {
                        borderColor: "primary.main",
                        boxShadow: 1,
                      },
                    }}
                  >
                    <CardActionArea
                      onClick={() => handleSelectType(card.type)}
                      sx={{
                        height: "100%",
                        p: 2.5,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-start",
                        gap: 1.5,
                      }}
                    >
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        width="100%"
                      >
                        {card.icon}
                        <Avatar
                          sx={{
                            width: 28,
                            height: 28,
                            fontSize: 13,
                            fontWeight: 600,
                            bgcolor: "primary100.main",
                            color: "primary1000.main",
                          }}
                        >
                          {count}
                        </Avatar>
                      </Stack>
                      <Typography variant="subtitle1" fontWeight={600}>
                        {card.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {card.description}
                      </Typography>
                    </CardActionArea>
                  </Card>
                );
              })}
            </Stack>
          ) : (
            <Box sx={{ minHeight: 240, position: "relative" }}>
              {selectedType === "prescription" ? (
                processedMedicalNoteData.length === 0 ? (
                  <NoDataWarning message="Nenhuma receita disponível para reemissão." />
                ) : (
                  <MedicalNotes
                    setRefetch={setRefetch}
                    sign
                    rowData={processedMedicalNoteData}
                    handleOpen={handleOpenViewer}
                    setBinaryUrl={setBinaryUrl}
                  />
                )
              ) : selectedType === "solicitation" ? (
                processedSolicitationData.length === 0 ? (
                  <NoDataWarning message="Nenhuma solicitação de exame disponível para reemissão." />
                ) : (
                  <Solicitations
                    sign
                    setRefetch={setRefetch}
                    handleOpen={handleOpenViewer}
                    setBinaryUrl={setBinaryUrl}
                    rowData={processedSolicitationData}
                    resultsData={resultsData}
                  />
                )
              ) : selectedType === "certificate" ? (
                processedNoteReportData.length === 0 ? (
                  <NoDataWarning message="Nenhum relatório ou atestado disponível para reemissão." />
                ) : (
                  <NoteReport
                    setRefetch={setRefetch}
                    sign
                    rowData={processedNoteReportData}
                    handleOpen={handleOpenViewer}
                    setBinaryUrl={setBinaryUrl}
                  />
                )
              ) : null}
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {viewerOpen && (
        <IframeDialog
          setRefetchSummary={setRefetch}
          handleClose={handleCloseViewer}
          isOpen={viewerOpen}
          data={binaryUrl}
        />
      )}
    </>
  );
}
